import React from "react";

interface Props {
  markdown: string;
}

const splitRow = (line: string): string[] =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map(cell => cell.trim());

const isSeparator = (line: string) => /^\s*\|?\s*:?-{2,}/.test(line);

/** Render a markdown pipe table from the mind; falls back to preformatted text. */
const MarkdownTable: React.FC<Props> = ({ markdown }) => {
  const lines = markdown
    .split("\n")
    .map(l => l.trim())
    .filter(l => l.startsWith("|") || l.includes("|"));

  if (lines.length < 2 || !isSeparator(lines[1])) {
    return (
      <pre className="overflow-x-auto whitespace-pre-wrap rounded-md bg-gray-50 p-3 text-xs text-gray-700">
        {markdown}
      </pre>
    );
  }

  const header = splitRow(lines[0]);
  const rows = lines.slice(2).map(splitRow);

  return (
    <div className="overflow-x-auto rounded-md border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            {header.map((cell, i) => (
              <th
                key={i}
                scope="col"
                className="whitespace-nowrap px-3 py-2 text-left text-xs font-semibold text-gray-700"
              >
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {rows.map((row, r) => (
            <tr key={r}>
              {header.map((_, c) => (
                <td key={c} className="px-3 py-2 align-top text-gray-700">
                  {row[c] ?? ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default MarkdownTable;
